import {
  YES_NO,
  applicantSection,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { entitySection } from "modules/ebiForms/definitions/sharedPlanningTaxes";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * A8. Deklaracja o wysokości opłaty za gospodarowanie odpadami komunalnymi — art. 6m ust. 1–2 i art. 6n ust. 1 ucpg
 * (wzór deklaracji, wykaz dokumentów i warunki składania elektronicznego określa rada gminy w uchwale). Brak wzoru
 * ogólnopolskiego — formularz OGÓLNY według ucpg: okoliczności (pierwsza / zmiana danych / korekta — wariant w BE),
 * składający i tytuł prawny, nieruchomość zamieszkała (metoda i stawka z uchwały, zwolnienia), niezamieszkała
 * (pojemniki) albo rekreacyjna (ryczałt), wysokość opłaty. Układ jak deklaracja DO-1 Rzeszowa (zał. nr 1 do uchwały
 * nr XXXIV/683/2025, https://bip.erzeszow.pl/static/img/k02/GK/DM/2025/Deklaracja_o_wysokosci_oplaty_za_gosp._odpadami_komunalnymi_DO-1.pdf,
 * pobrana i odczytana 22.10.2026). Metody, stawki, zwolnienia i wymagane dokumenty są per gmina — konfiguracja
 * (wariant uchwały) do potwierdzenia z wydziałem; EBI nie liczy kwot, petent podaje je jak w deklaracji papierowej.
 */
const FIRST: EbiCondition = { field: "okolicznosci", equals: "pierwsza" };
const CHANGE: EbiCondition = { field: "okolicznosci", equals: "zmiana" };
const CHANGE_OR_CORRECTION: EbiCondition = { field: "okolicznosci", in: ["zmiana", "korekta"] };
const ENTITY: EbiCondition = { field: "skladajacy", in: ["osobaPrawna", "jednostka"] };
const INHABITED: EbiCondition = { field: "rodzajNieruchomosci", in: ["zamieszkala", "mieszana"] };
const UNINHABITED: EbiCondition = { field: "rodzajNieruchomosci", in: ["niezamieszkala", "mieszana"] };
const RECREATIONAL: EbiCondition = { field: "rodzajNieruchomosci", equals: "rekreacyjna" };
const SINGLE_FAMILY: EbiCondition = { all: [INHABITED, { field: "zabudowa", equals: "jednorodzinna" }] };
const OTHER_EXEMPTION: EbiCondition = { all: [INHABITED, { field: "inneZwolnienie", equals: "tak" }] };

const A8: IEbiFormDefinition = {
  code: "A8",
  version: 1,
  title: "Deklaracja o wysokości opłaty za gospodarowanie odpadami komunalnymi",
  category: EEbiFormCategory.Taxes,
  office: EEbiFormOffice.Municipality,
  documentType: "Deklaracja",
  description: "Pierwsza deklaracja, nowa deklaracja po zmianie danych (np. liczby mieszkańców) albo korekta — opłata za odbiór odpadów.",
  keywords: ["odpady", "śmieci", "opłata za odpady", "deklaracja śmieciowa", "gospodarowanie odpadami", "kompostownik"],
  organ: "Wójt / Burmistrz / Prezydent Miasta",
  legalBasis:
    "art. 6m ust. 1–2 i art. 6n ust. 1 ustawy z dnia 13 września 1996 r. o utrzymaniu czystości i porządku w gminach; uchwała rady gminy w sprawie wzoru deklaracji",
  request: "Składam deklarację o wysokości opłaty za gospodarowanie odpadami komunalnymi dla nieruchomości wskazanej poniżej.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty; pełnomocnictwo — 17 zł.",
  deadlineLabel: "w ciągu 14 dni",
  deadline:
    "Pierwsza deklaracja — 14 dni od zamieszkania pierwszego mieszkańca lub powstania odpadów; nowa deklaracja — do 10. dnia miesiąca następującego po miesiącu zmiany (po śmierci mieszkańca — do 6 miesięcy).",
  variantField: "okolicznosci",
  variants: {
    pierwsza: { title: "Deklaracja o wysokości opłaty za gospodarowanie odpadami komunalnymi — pierwsza" },
    zmiana: { title: "Deklaracja o wysokości opłaty za gospodarowanie odpadami komunalnymi — zmiana danych" },
    korekta: { title: "Korekta deklaracji o wysokości opłaty za gospodarowanie odpadami komunalnymi" },
  },
  sections: [
    {
      code: "okolicznosci",
      title: "Okoliczności złożenia deklaracji",
      fields: [
        {
          code: "okolicznosci",
          type: "radio",
          label: "Składam",
          required: true,
          options: [
            { value: "pierwsza", label: "pierwszą deklarację" },
            { value: "zmiana", label: "nową deklarację — zmiana danych będących podstawą ustalenia wysokości opłaty" },
            { value: "korekta", label: "korektę deklaracji (nieprawidłowo wypełniona deklaracja)" },
          ],
        },
        {
          code: "dataPowstania",
          type: "date",
          label: "Data zamieszkania na nieruchomości pierwszego mieszkańca albo powstania odpadów komunalnych",
          required: true,
          visibleWhen: FIRST,
          width: "half",
        },
        { code: "dataZmiany", type: "date", label: "Data zaistnienia zmiany", required: true, visibleWhen: CHANGE_OR_CORRECTION, width: "half" },
        {
          code: "przyczynaZmiany",
          type: "textarea",
          label: "Przyczyna zmiany lub korekty",
          requiredWhen: CHANGE,
          visibleWhen: CHANGE_OR_CORRECTION,
          maxLength: 1000,
          placeholder: "np. zmiana liczby mieszkańców, zmiana właściciela, kompostownik",
        },
      ],
    },
    applicantSection("Składający deklarację"),
    { ...representationSection(), title: "W czyim imieniu składasz deklarację" },
    {
      code: "wlasciciel",
      title: "Właściciel nieruchomości",
      fields: [
        {
          code: "skladajacy",
          type: "radio",
          label: "Właściciel nieruchomości (zobowiązany do opłaty) to",
          required: true,
          defaultValue: "osobaFizyczna",
          options: [
            { value: "osobaFizyczna", label: "osoba fizyczna — wnioskodawca albo mocodawca" },
            { value: "osobaPrawna", label: "osoba prawna" },
            { value: "jednostka", label: "jednostka organizacyjna nieposiadająca osobowości prawnej (np. wspólnota mieszkaniowa)" },
          ],
        },
        {
          code: "tytulPrawny",
          type: "radio",
          label: "Tytuł prawny do nieruchomości",
          required: true,
          options: [
            { value: "wlasciciel", label: "właściciel" },
            { value: "wspolwlasciciel", label: "współwłaściciel" },
            { value: "uzytkownikWieczysty", label: "użytkownik wieczysty" },
            { value: "zarzad", label: "jednostka organizacyjna lub osoba posiadająca nieruchomość w zarządzie lub użytkowaniu" },
            { value: "zarzadWspolny", label: "zarząd nieruchomości wspólnej (wspólnota mieszkaniowa, spółdzielnia mieszkaniowa)" },
            { value: "inny", label: "inny podmiot władający nieruchomością" },
          ],
        },
        {
          code: "wspolwlasciciele",
          type: "repeat",
          label: "Pozostali współwłaściciele (nieobowiązkowo — gdy uchwała rady gminy wymaga ich danych)",
          itemLabel: "Współwłaściciel",
          visibleWhen: { field: "tytulPrawny", equals: "wspolwlasciciel" },
          max: 20,
          fields: [
            { code: "nazwa", type: "text", label: "Imię i nazwisko", width: "half", maxLength: 200 },
            { code: "pesel", type: "pesel", label: "PESEL", width: "half" },
          ],
        },
      ],
    },
    entitySection("Właściciel nieruchomości — osoba prawna albo jednostka organizacyjna", ENTITY),
    {
      code: "nieruchomosc",
      title: "Nieruchomość, na której powstają odpady komunalne",
      fields: [
        { code: "nieruchomoscAdres", type: "text", label: "Ulica, numer domu i lokalu", required: true, maxLength: 200 },
        { code: "nieruchomoscKodPocztowy", type: "postalCode", label: "Kod pocztowy", required: true, width: "third", placeholder: "00-000" },
        { code: "nieruchomoscMiejscowosc", type: "text", label: "Miejscowość", required: true, width: "twoThirds", maxLength: 100 },
        {
          code: "nieruchomoscDzialka",
          type: "text",
          label: "Obręb i numer działki (gdy nieruchomość nie ma adresu)",
          maxLength: 100,
          placeholder: "np. obręb 0003, dz. 125/4",
        },
        {
          code: "rodzajNieruchomosci",
          type: "radio",
          label: "Nieruchomość jest",
          required: true,
          options: [
            { value: "zamieszkala", label: "zamieszkała" },
            { value: "niezamieszkala", label: "niezamieszkała, na której powstają odpady komunalne" },
            { value: "mieszana", label: "w części zamieszkała, a w części niezamieszkała, na której powstają odpady komunalne" },
            { value: "rekreacyjna", label: "domkiem letniskowym albo nieruchomością wykorzystywaną na cele rekreacyjno-wypoczynkowe" },
          ],
        },
        {
          code: "zabudowa",
          type: "radio",
          label: "Rodzaj zabudowy",
          required: true,
          visibleWhen: INHABITED,
          options: [
            { value: "jednorodzinna", label: "jednorodzinna" },
            { value: "wielolokalowa", label: "wielolokalowa" },
          ],
        },
      ],
    },
    {
      code: "zamieszkala",
      title: "Nieruchomość zamieszkała — wyliczenie opłaty",
      visibleWhen: INHABITED,
      description: "Metodę ustalenia opłaty, stawkę i zwolnienia określa uchwała rady gminy — przyjmij je z uchwały obowiązującej w gminie.",
      fields: [
        {
          code: "metoda",
          type: "radio",
          label: "Opłatę ustala się od",
          required: true,
          options: [
            { value: "mieszkancy", label: "liczby mieszkańców zamieszkujących nieruchomość" },
            { value: "woda", label: "ilości zużytej wody z nieruchomości" },
            { value: "powierzchnia", label: "powierzchni lokalu mieszkalnego" },
            { value: "gospodarstwo", label: "gospodarstwa domowego" },
          ],
        },
        {
          code: "liczbaMieszkancow",
          type: "number",
          label: "Liczba mieszkańców zamieszkujących nieruchomość",
          required: true,
          visibleWhen: { field: "metoda", equals: "mieszkancy" },
          min: 0,
          width: "half",
        },
        {
          code: "zuzycieWody",
          type: "number",
          label: "Ilość zużytej wody [m³ w okresie z uchwały]",
          required: true,
          visibleWhen: { field: "metoda", equals: "woda" },
          decimals: 2,
          min: 0,
          width: "half",
        },
        {
          code: "powierzchniaLokalu",
          type: "number",
          label: "Powierzchnia lokalu mieszkalnego [m²]",
          required: true,
          visibleWhen: { field: "metoda", equals: "powierzchnia" },
          decimals: 2,
          min: 0,
          width: "half",
        },
        {
          code: "liczbaGospodarstw",
          type: "number",
          label: "Liczba gospodarstw domowych",
          required: true,
          visibleWhen: { field: "metoda", equals: "gospodarstwo" },
          min: 0,
          width: "half",
        },
        { code: "stawka", type: "number", label: "Stawka opłaty z uchwały rady gminy [zł]", required: true, decimals: 2, min: 0, width: "half" },
        {
          code: "kompostownik",
          type: "radio",
          label: "Czy kompostujesz bioodpady stanowiące odpady komunalne w kompostowniku przydomowym i korzystasz ze zwolnienia w części z opłaty?",
          required: true,
          visibleWhen: SINGLE_FAMILY,
          options: YES_NO,
        },
        {
          code: "zwolnienieKompostownik",
          type: "number",
          label: "Kwota zwolnienia z tytułu kompostowania [zł miesięcznie]",
          required: true,
          visibleWhen: { all: [SINGLE_FAMILY, { field: "kompostownik", equals: "tak" }] },
          decimals: 2,
          min: 0,
          width: "half",
        },
        {
          code: "inneZwolnienie",
          type: "radio",
          label: "Czy przysługuje Ci inne zwolnienie przewidziane w uchwale rady gminy (np. dla rodzin wielodzietnych albo zależne od dochodu)?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
        {
          code: "inneZwolnieniePodstawa",
          type: "text",
          label: "Rodzaj zwolnienia i przepis uchwały",
          required: true,
          visibleWhen: OTHER_EXEMPTION,
          width: "half",
          maxLength: 300,
        },
        {
          code: "inneZwolnienieKwota",
          type: "number",
          label: "Kwota zwolnienia [zł miesięcznie]",
          required: true,
          visibleWhen: OTHER_EXEMPTION,
          decimals: 2,
          min: 0,
          width: "half",
        },
        {
          code: "oplataZamieszkala",
          type: "number",
          label: "Miesięczna opłata za część zamieszkałą, po odliczeniu zwolnień [zł]",
          required: true,
          decimals: 2,
          min: 0,
          width: "half",
        },
      ],
    },
    {
      code: "niezamieszkala",
      title: "Nieruchomość niezamieszkała — pojemniki lub worki",
      visibleWhen: UNINHABITED,
      description: "Pojemność, liczbę i częstotliwość odbioru pojemników oraz stawki przyjmij z uchwały rady gminy i regulaminu utrzymania czystości.",
      fields: [
        {
          code: "pojemniki",
          type: "repeat",
          label: "Pojemniki lub worki na odpady komunalne",
          itemLabel: "Pojemnik",
          required: true,
          min: 1,
          max: 20,
          fields: [
            { code: "pojemnosc", type: "text", label: "Rodzaj i pojemność pojemnika lub worka", required: true, width: "half", maxLength: 100, placeholder: "np. pojemnik 120 l" },
            { code: "liczba", type: "number", label: "Liczba pojemników lub worków w miesiącu", required: true, min: 1, width: "half" },
            { code: "stawka", type: "number", label: "Stawka opłaty za pojemnik lub worek [zł]", required: true, decimals: 2, min: 0, width: "half" },
            { code: "kwota", type: "number", label: "Kwota [zł miesięcznie]", required: true, decimals: 2, min: 0, width: "half" },
          ],
        },
        {
          code: "oplataNiezamieszkala",
          type: "number",
          label: "Miesięczna opłata za część niezamieszkałą [zł]",
          required: true,
          decimals: 2,
          min: 0,
          width: "half",
        },
      ],
    },
    {
      code: "rekreacyjna",
      title: "Domek letniskowy albo nieruchomość rekreacyjno-wypoczynkowa — ryczałt",
      visibleWhen: RECREATIONAL,
      fields: [
        { code: "liczbaDomkow", type: "number", label: "Liczba domków letniskowych lub nieruchomości", required: true, min: 1, defaultValue: "1", width: "half" },
        { code: "ryczalt", type: "number", label: "Roczna ryczałtowa stawka opłaty z uchwały rady gminy [zł]", required: true, decimals: 2, min: 0, width: "half" },
        { code: "oplataRoczna", type: "number", label: "Roczna wysokość opłaty [zł]", required: true, decimals: 2, min: 0, width: "half" },
      ],
    },
    {
      code: "oplata",
      title: "Wysokość opłaty",
      fields: [
        {
          code: "oplataMiesieczna",
          type: "number",
          label: "Miesięczna wysokość opłaty za gospodarowanie odpadami komunalnymi [zł]",
          required: true,
          visibleWhen: { field: "rodzajNieruchomosci", in: ["zamieszkala", "niezamieszkala", "mieszana"] },
          decimals: 2,
          min: 0,
          width: "half",
          hint: "Suma opłat za część zamieszkałą i niezamieszkałą, po odliczeniu zwolnień.",
        },
        {
          code: "pouczenie",
          type: "info",
          label:
            "Pouczenie: deklaracja stanowi podstawę do wystawienia tytułu wykonawczego (art. 3a ustawy o postępowaniu egzekucyjnym w administracji). W razie niezłożenia deklaracji albo uzasadnionych wątpliwości co do zawartych w niej danych wysokość opłaty określa w drodze decyzji wójt, burmistrz lub prezydent miasta (art. 6o ustawy o utrzymaniu czystości i porządku w gminach).",
        },
      ],
    },
    replySection("Sposób doręczania korespondencji w sprawie"),
  ],
  attachments: [
    {
      code: "zalacznikUchwaly",
      label: "Załącznik do deklaracji według wzoru z uchwały rady gminy (np. dane współwłaścicieli, kolejnych nieruchomości lub lokali)",
      hint: "Gdy uchwała rady gminy go przewiduje — nieobowiązkowo.",
      multiple: true,
    },
    {
      code: "dokumentZwolnienia",
      label: "Dokument potwierdzający prawo do zwolnienia (np. Karta Dużej Rodziny, zaświadczenie o dochodach)",
      hint: "Gdy uchwała rady gminy go wymaga.",
      visibleWhen: OTHER_EXEMPTION,
    },
    {
      code: "dokumentZmiany",
      label: "Dokument potwierdzający zmianę (np. kopia aktu notarialnego, aktu zgonu)",
      hint: "Praktyka urzędów — nieobowiązkowo.",
      visibleWhen: CHANGE,
    },
    {
      code: "umocowanieZarzadu",
      label: "Dokument umocowania zarządu nieruchomości wspólnej (np. uchwała wspólnoty o wyborze zarządu, umowa o zarządzanie)",
      visibleWhen: { field: "tytulPrawny", equals: "zarzadWspolny" },
      required: true,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default A8;
